import { createContext, useContext, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useSubscription from '@src/hooks/useSubscription';
import QueryKeys from '@src/constants/queryKeys';
import { UUID } from 'crypto';
import { DataPointWithValue } from '@src/types/DataPoint';
import { store } from '@store';
interface ConfigContextType {
  controlAuthority: number | undefined;
}

interface SubscriptionResult {
  modesUpdate: DataPointWithValue | undefined;
  controlAuthority: number | undefined;
}

const ConfigContext = createContext<ConfigContextType>({
  controlAuthority: undefined,
});

export default function ConfigProvider({
  children,
}: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { siteMeta } = store.getState().config;

  const modesUpdateTopics = useMemo(
    () => [
      {
        deviceType: '',
        displayName: '',
        pointName: 'ModesUpdate',
        dataType: 'boolean_integer',
        fullTopic: 'site/control-modes',
        siteId: '',
        controllerId: '',
        id: '' as UUID,
        createdAt: '',
        updatedAt: '',
      },
      {
        deviceType: '',
        displayName: '',
        pointName: 'ControlMode_Authority',
        dataType: 'integer',
        fullTopic: `ems/site/${siteMeta.siteId}/root/ControlMode_Authority/integer`,
        siteId: '',
        controllerId: '',
        id: '' as UUID,
        createdAt: '',
        updatedAt: '',
      },
    ],
    [siteMeta],
  );

  const controlUpdates = useSubscription(modesUpdateTopics);

  const { modesUpdate, controlAuthority } = useMemo(() => {
    const result: SubscriptionResult = {
      modesUpdate: undefined,
      controlAuthority: undefined,
    };
    controlUpdates.forEach((dp: DataPointWithValue) => {
      if (dp.pointName === 'ModesUpdate') {
        result.modesUpdate = dp;
      } else if (dp.pointName === 'ControlMode_Authority') {
        result.controlAuthority = dp.value as number;
      }
    });
    return {
      modesUpdate: result.modesUpdate,
      controlAuthority: result.controlAuthority,
    };
  }, [controlUpdates]);

  useEffect(() => {
    if (modesUpdate?.value !== undefined) {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.controlModeTemplates],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.stacks],
      });
    }
  }, [modesUpdate, queryClient]);

  const contextValue = useMemo(
    () => ({
      controlAuthority,
    }),
    [controlAuthority],
  );

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfigContext(): ConfigContextType {
  const context = useContext(ConfigContext);
  return context;
}
